import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  PLATFORM_ID,
  Provider,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  ExtraOptions,
  InMemoryScrollingFeature,
  InMemoryScrollingOptions,
  PreloadAllModules,
  provideRouter,
  withInMemoryScrolling,
} from '@angular/router';

import { routes } from './app.routes';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { HttpClient, provideHttpClient, withFetch } from '@angular/common/http';
import { provideTranslateService, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { environment } from '../environments/environment';
import { AngularRouterTracking, Angulartics2, ANGULARTICS2_TOKEN, Angulartics2Settings, RouterlessTracking } from 'angulartics2';
import { BsModalService } from 'ngx-bootstrap/modal';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import localePtpt from '@angular/common/locales/pt-PT';
import localeEs from '@angular/common/locales/es';
import { APP_BASE_HREF, isPlatformServer, registerLocaleData } from '@angular/common';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getDatabase, provideDatabase } from '@angular/fire/database';
import { defineLocale, enGbLocale, esLocale, ptBrLocale } from 'ngx-bootstrap/chronos';
import Bugsnag from '@bugsnag/js';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';

import * as translationPt from '../assets/i18n-lang/pt.json';
import * as translationEn from '../assets/i18n-lang/en.json';
import * as translationEs from '../assets/i18n-lang/es.json';
import { of } from 'rxjs';
import { IntlService } from './core/services/intl.service';

const TRANSLATIONS = {
  en: translationEn,
  pt: translationPt,
  es: translationEs,
};

registerLocaleData(localePtpt, 'pt');
registerLocaleData(localeEs, 'es');

defineLocale('pt', ptBrLocale);
defineLocale('es', esLocale);
defineLocale('en', enGbLocale);

Bugsnag.start({
  apiKey: environment.bugsnagKey,
  enabledReleaseStages: ['prd'],
  releaseStage: environment.production ? 'prd' : 'local',
  onError: function (event) {
    // Matches lazy-chunk load failures across builders/browsers:
    // - Webpack: "Loading chunk N failed"
    // - esbuild/native dynamic import (Angular 19): Chrome/Firefox/Safari variants below
    const chunkFailedMessage =
      /Loading chunk [\d]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;
    if (chunkFailedMessage.test(event?.originalError?.message)) {
      window.location.reload();
      return false;
    }

    if (event?.originalError?.url) {
      event.addMetadata('url', event?.originalError?.url);
    }

    if (event?.originalError?.functional) {
      // do not log on functional error
      return false;
    }
  },
});

export function errorHandlerFactory() {
  return new BugsnagErrorHandler();
}

export function httpLoaderFactory(http: HttpClient) {
  const platformId = inject(PLATFORM_ID);
  if (isPlatformServer(platformId)) {
    // Return a loader object with a getTranslation method for SSR
    return {
      getTranslation: (lang: string) => {
        return of(TRANSLATIONS[lang] || TRANSLATIONS['pt']);
      },
    };
  }

  // On the browser, use the HTTP loader to fetch translation files
  if (environment.buildHash) {
    return new TranslateHttpLoader(http, './assets/i18n-lang/', '.json?v=' + environment.buildHash);
  } else {
    return new TranslateHttpLoader(http, './assets/i18n-lang/', '.json');
  }
}

export function provideAngulartics(settings: Partial<Angulartics2Settings>): Provider[] {
  return [
    { provide: ANGULARTICS2_TOKEN, useValue: { settings } },
    { provide: RouterlessTracking, useClass: AngularRouterTracking },
    Angulartics2,
  ];
}

const scrollConfig: ExtraOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
  preloadingStrategy: PreloadAllModules,
};
const inMemoryScrollingFeature: InMemoryScrollingFeature = withInMemoryScrolling(scrollConfig);

export const appConfig: ApplicationConfig = {
  providers: [
    provideFirebaseApp(() => {
      const platformId = inject(PLATFORM_ID);
      if (isPlatformServer(platformId)) {
        // Return a dummy app for server-side rendering
        return initializeApp({
          ...environment.firebase,
          projectId: 'dummy-project',
          appId: 'dummy-app',
        });
      }
      return initializeApp(environment.firebase);
    }),
    provideDatabase(() => getDatabase()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, inMemoryScrollingFeature),
    provideHttpClient(withFetch()),
    provideTranslateService({ defaultLanguage: 'pt' }),
    provideAngulartics({
      developerMode: !environment.production,
    }),
    provideAnimationsAsync(),
    BsModalService,
    importProvidersFrom([
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: httpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ]),
    provideClientHydration(withEventReplay()),
    { provide: ErrorHandler, useFactory: errorHandlerFactory },
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: LOCALE_ID, deps: [IntlService], useFactory: (intl: IntlService) => intl.getCurrentLanguage() },
  ],
};
